import { makeStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import IconButton from "@material-ui/core/IconButton";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Skeleton from "@saleor/components/Skeleton";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import useMallQuery from "@saleor/searches/useMallQuery";
import { StaffMemberDetails_user_malls } from "@saleor/staff/types/StaffMemberDetails";
import { FetchMoreProps } from "@saleor/types";
import { SmopStatusEnum } from "@saleor/types/globalTypes";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { maybe } from "../../../misc";
import StaffAssignMallDialog from "../StaffAssignMallDialog";
import { StaffDetailsFormData } from "../StaffCreatePage";

export interface MallListProps {
  disabled: boolean;
  emptyText: React.ReactNode;
  title: React.ReactNode;
  onMallAssign?: () => void;
  onMallUnassign?: (id: string) => void;
  malls?: SearchMalls_search_edges_node[];
  mallsAssigned?: StaffMemberDetails_user_malls;
  onUpdateSelectedMalls?: (selectedMalls: string[]) => void;
  fetchMoreMalls: FetchMoreProps;
  formData: StaffDetailsFormData;
}

const useStyles = makeStyles(
  theme => ({
    buttonDisabled: {
      "& > span": {
        color: "#616161"
      }
    },
    iconCell: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 48 + theme.spacing(2)
    },
    indicator: {
      color: theme.palette.text.disabled,
      display: "inline-block",
      left: 0,
      marginRight: theme.spacing(0.5),
      position: "absolute"
    },
    offsetCell: {
      "&:first-child": {
        paddingLeft: theme.spacing(3)
      },
      position: "relative",
      width: "90%"
    },
    pointer: {
      cursor: "pointer"
    },
    root: {
      "&:last-child": {
        paddingBottom: 0
      },
      paddingTop: 0
    },
    rotate: {
      transform: "rotate(180deg)"
    },
    textCenter: {
      textAlign: "center"
    },
    textRight: {
      textAlign: "right"
    },
    toLeft: {
      "&:first-child": {
        paddingLeft: 0
      },
      width: "100%"
    },
    wideColumn: {
      width: "100%"
    }
  }),
  { name: "StaffMallList" }
);

const StaffMallList: React.FC<MallListProps> = props => {
  const { disabled, title, malls, formData } = props;

  const classes = useStyles(props);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCollapsed, setCollapseStatus] = useState(true);
  const [selectedMalls, setSelectedMalls] = useState([]);
  const [confirmedMallIds, setConfirmedMallIds] = useState(
    formData.malls || []
  );
  const [displayedTotalCount, setDisplayedTotalCount] = useState(
    selectedMalls.length
  );

  const toggleCollapse = () => setCollapseStatus(!isCollapsed);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  const mallInitialSearchData = {
    after: null,
    first: 100,
    query: "",
    statuses: [SmopStatusEnum.ACT]
  };

  const {
    loadMore: loadMoreMalls,
    search: searchMall,
    result: searchMallsOpts
  } = useMallQuery({
    variables: mallInitialSearchData
  });

  const handleConfirmSelection = confirmedMalls => {
    const confirmedMallIds = confirmedMalls.map(mall => mall.id);

    setSelectedMalls(confirmedMalls);
    setConfirmedMallIds(confirmedMallIds);
    handleCloseDialog();
    if (props.onUpdateSelectedMalls) {
      props.onUpdateSelectedMalls(confirmedMallIds);
    }
  };

  const onMallUnassign = id => {
    const updatedMalls = selectedMalls.filter(mall => mall?.id !== id);
    const updatedMallIds = confirmedMallIds.filter(mallId => mallId !== id);
    setSelectedMalls(updatedMalls);
    setConfirmedMallIds(updatedMallIds);
    if (props.onUpdateSelectedMalls) {
      props.onUpdateSelectedMalls(updatedMallIds);
    }
  };

  useEffect(() => {
    setSelectedMalls(malls || []);
    setConfirmedMallIds(formData.malls);
  }, [malls]);

  const totalCount = selectedMalls.length;

  useEffect(() => {
    if (totalCount === 0) {
      setCollapseStatus(true);
    }
    setDisplayedTotalCount(totalCount);
  }, [totalCount]);

  useEffect(() => {
    setDisplayedTotalCount(selectedMalls.length);
  }, [selectedMalls.length]);

  const appliedToAllMalls = totalCount === 0;

  useEffect(() => {
    if (searchMallsOpts?.data?.search?.pageInfo?.hasNextPage) {
      loadMoreMalls();
    }
  }, [searchMallsOpts, loadMoreMalls]);

  return (
    <Card>
      <CardTitle
        title={title}
        toolbar={
          <Button
            id="assignMallsBtn"
            color="primary"
            disabled={disabled}
            className={disabled ? classes.buttonDisabled : null}
            onClick={handleOpenDialog}
          >
            <FormattedMessage
              defaultMessage="Assign Malls"
              description="button"
            />
          </Button>
        }
      />
      <CardContent className={classes.root}>
        <TableBody>
          <TableRow
            className={classes.pointer}
            onClick={() => {
              if (totalCount > 0) {
                toggleCollapse();
              }
            }}
          >
            <TableCell
              className={classNames(classes.wideColumn, classes.toLeft)}
            >
              {selectedMalls.length === 0 && disabled ? (
                <Skeleton />
              ) : selectedMalls.length === 0 ? (
                <FormattedMessage
                  defaultMessage="Applied to all malls"
                  description="number of malls"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="{number} Mall{plural}"
                  description="number of malls"
                  values={{
                    number: displayedTotalCount,
                    plural: displayedTotalCount > 1 ? "s" : ""
                  }}
                />
              )}
            </TableCell>
            <TableCell
              className={classNames(classes.textRight, classes.iconCell)}
            >
              {displayedTotalCount > 0 && (
                <IconButton>
                  <ArrowDropDownIcon
                    className={classNames({ [classes.rotate]: !isCollapsed })}
                  />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
          {!isCollapsed && selectedMalls.length > 0 ? (
            selectedMalls.map(mall => (
              <TableRow key={mall?.id}>
                <TableCell className={classes.offsetCell}>
                  {mall?.name}
                </TableCell>
                <TableCell
                  className={classNames(classes.textRight, classes.iconCell)}
                >
                  <IconButton
                    color="primary"
                    disabled={disabled}
                    onClick={() => onMallUnassign(mall.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <></>
            </TableRow>
          )}
        </TableBody>
      </CardContent>
      <StaffAssignMallDialog
        malls={searchMallsOpts.data?.search.edges.map(edge => edge.node) || []}
        field="mall"
        initial={selectedMalls}
        open={dialogOpen}
        formData={formData}
        onFetch={searchMall}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmSelection}
        loading={searchMallsOpts.loading}
        hasMore={searchMallsOpts?.data?.search?.pageInfo.hasNextPage}
        onFetchMore={loadMoreMalls}
        appliedToAllMalls={appliedToAllMalls}
        fetchMoreMalls={{
          hasMore: maybe(
            () =>
              searchMallsOpts &&
              searchMallsOpts.data &&
              searchMallsOpts.data.search.pageInfo.hasNextPage
          ),
          loading: searchMallsOpts.loading,
          onFetchMore: loadMoreMalls
        }}
      />
    </Card>
  );
};

export default StaffMallList;
